/**
 * OpsWorkflowNav — compact horizontal navigation strip shared by the four
 * Operations Center views: Daily Command Center, Lead Workflow,
 * Client Workflow, and Production Workflow.
 *
 * Rendered at the top of each operational page.
 * Uses established color identifiers: Blue / Amber / Teal / Violet.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { Activity, SquareKanban, Workflow, Factory } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  {
    key: "live_ops",
    label: "Daily Command Center",
    path: "/",
    Icon: Activity,
    color: "#2F80ED",
    activeBg: "bg-[#2F80ED]",
    hoverBg: "hover:bg-[#2F80ED]/15",
    iconColor: "text-[#2F80ED]",
    borderColor: "border-[#2F80ED]",
  },
  {
    key: "sales",
    label: "Lead Workflow",
    path: "/sales/board",
    Icon: SquareKanban,
    color: "#D97706",
    activeBg: "bg-amber-600",
    hoverBg: "hover:bg-amber-600/15",
    iconColor: "text-amber-400",
    borderColor: "border-amber-500",
  },
  {
    key: "client_success",
    label: "Client Workflow",
    path: "/client-success",
    Icon: Workflow,
    color: "#0D9488",
    activeBg: "bg-teal-700",
    hoverBg: "hover:bg-teal-700/15",
    iconColor: "text-teal-400",
    borderColor: "border-teal-500",
  },
  {
    key: "production",
    label: "Production Workflow",
    path: "/production",
    Icon: Factory,
    color: "#7C3AED",
    activeBg: "bg-violet-700",
    hoverBg: "hover:bg-violet-700/15",
    iconColor: "text-violet-400",
    borderColor: "border-violet-500",
  },
];

export default function OpsWorkflowNav() {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div
      className="shrink-0 flex items-center gap-1 px-4 py-2 bg-[#F2F5F9] border-b border-slate-200"
      data-testid="ops-workflow-nav"
    >
      {NAV_ITEMS.map(({ key, label, path, Icon, activeBg, hoverBg, iconColor, borderColor }) => {
        const isActive =
          path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
        return (
          <button
            key={key}
            onClick={() => navigate(path)}
            data-testid={`ops-nav-${key}`}
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border",
              isActive
                ? cn(activeBg, "text-white border-transparent shadow-sm")
                : cn("text-slate-600 border-transparent bg-transparent", hoverBg, "hover:text-slate-800")
            )}
          >
            <Icon
              className={cn("h-3.5 w-3.5 shrink-0", isActive ? "text-white" : iconColor)}
              strokeWidth={2}
            />
            <span className="truncate hidden sm:block">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
